import {
    Box,
    Card,
    Chip,
    Grid,
    Typography,
    alpha,
    Button,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    LinearProgress,
    IconButton,
    Tooltip,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import {
    socialMediaService,
    PLATFORM_INFO,
} from "../../../api/services/socialMediaService";
import { toast } from "react-toastify";
import CampaignRoundedIcon from "@mui/icons-material/CampaignRounded";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ScheduleRoundedIcon from "@mui/icons-material/ScheduleRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import ThumbUpRoundedIcon from "@mui/icons-material/ThumbUpRounded";
import TouchAppRoundedIcon from "@mui/icons-material/TouchAppRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

/* ─── Shared Premium Styles ─── */
const premiumCard = {
    borderRadius: "16px",
    border: "1px solid rgba(226, 232, 240, 0.6)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.02)",
    overflow: "hidden",
    transition: "all 0.25s cubic-bezier(0.4,0,0.2,1)",
    "&:hover": {
        boxShadow: "0 4px 20px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.03)",
        transform: "translateY(-1px)",
    },
};

const sectionIconBox = (color: string) => ({
    width: 36,
    height: 36,
    borderRadius: "10px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: alpha(color, 0.08),
    border: `1px solid ${alpha(color, 0.12)}`,
    color: color,
});

/* ─── Stats Card Component ─── */
const StatsCard = ({
    icon,
    label,
    value,
    color,
    subtitle,
}: {
    icon: React.ReactNode;
    label: string;
    value: number | string;
    color: string;
    subtitle?: string;
}) => (
    <Card sx={premiumCard}>
        <Box
            sx={{
                position: "relative",
                p: 3,
                "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    height: "3px",
                    background: `linear-gradient(90deg, ${color}, ${alpha(color, 0.4)})`,
                },
            }}
        >
            <Box
                display="flex"
                alignItems="flex-start"
                justifyContent="space-between"
            >
                <Box>
                    <Typography
                        sx={{
                            fontSize: "12px",
                            fontWeight: 600,
                            color: "#94A3B8",
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            mb: 1,
                        }}
                    >
                        {label}
                    </Typography>
                    <Typography
                        sx={{
                            fontSize: "32px",
                            fontWeight: 800,
                            color: "#0F172A",
                            letterSpacing: "-0.03em",
                            lineHeight: 1,
                            mb: subtitle ? 0.5 : 0,
                        }}
                    >
                        {typeof value === "number"
                            ? new Intl.NumberFormat().format(value)
                            : value}
                    </Typography>
                    {subtitle && (
                        <Typography sx={{ fontSize: "12px", color: "#64748B", mt: 0.5 }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Box sx={sectionIconBox(color)}>{icon}</Box>
            </Box>
        </Box>
    </Card>
);

/* ─── Platform Health Card ─── */
const PlatformHealthCard = ({
    platform,
    accountName,
    tokenHealthy,
    tokenExpires,
}: {
    platform: string;
    accountName: string;
    tokenHealthy: boolean;
    tokenExpires?: string;
}) => {
    const info = PLATFORM_INFO[platform] || {
        name: platform,
        color: "#666",
        icon: "?",
    };
    const daysLeft = tokenExpires
        ? Math.ceil(
            (new Date(tokenExpires).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
        )
        : null;

    return (
        <Card sx={{ ...premiumCard, p: 2 }}>
            <Box display="flex" alignItems="center" gap={1.5}>
                <Box
                    sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "10px",
                        backgroundColor: alpha(info.color, 0.08),
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: "20px",
                    }}
                >
                    {info.icon}
                </Box>
                <Box flex={1}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
                        {info.name}
                    </Typography>
                    <Typography sx={{ fontSize: "12px", color: "#64748B" }}>
                        {accountName}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={0.5}>
                    {tokenHealthy ? (
                        <Chip
                            icon={<CheckCircleRoundedIcon sx={{ fontSize: 14 }} />}
                            label="Connected"
                            size="small"
                            sx={{
                                backgroundColor: "#F0FDF4",
                                color: "#16A34A",
                                fontWeight: 600,
                                fontSize: "11px",
                            }}
                        />
                    ) : (
                        <Chip
                            icon={<ErrorRoundedIcon sx={{ fontSize: 14 }} />}
                            label="Expired"
                            size="small"
                            sx={{
                                backgroundColor: "#FEF2F2",
                                color: "#DC2626",
                                fontWeight: 600,
                                fontSize: "11px",
                            }}
                        />
                    )}
                    {daysLeft !== null && daysLeft < 7 && daysLeft > 0 && (
                        <Tooltip title={`Token expires in ${daysLeft} days`}>
                            <WarningAmberRoundedIcon
                                sx={{ fontSize: 16, color: "#D97706" }}
                            />
                        </Tooltip>
                    )}
                </Box>
            </Box>
        </Card>
    );
};

/* ─── Campaign Status Config ─── */
const campaignStatusConfig: Record<
    string,
    { bg: string; color: string; label: string }
> = {
    draft: { bg: "#F1F5F9", color: "#64748B", label: "Draft" },
    generating: { bg: "#EFF6FF", color: "#2563EB", label: "Generating" },
    ready: { bg: "#FFF7ED", color: "#EA580C", label: "Ready" },
    approved: { bg: "#F0FDF4", color: "#16A34A", label: "Approved" },
    posting: { bg: "#EFF6FF", color: "#2563EB", label: "Posting" },
    completed: { bg: "#F0FDF4", color: "#16A34A", label: "Completed" },
    partial: { bg: "#FFFBEB", color: "#D97706", label: "Partial" },
    failed: { bg: "#FEF2F2", color: "#DC2626", label: "Failed" },
};

/* ─── Main Dashboard ─── */
const SocialDashboard = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { data: dashboardData, isLoading } = useQuery(
        "social-dashboard",
        () => socialMediaService.getDashboard(),
        { refetchInterval: 60000 }
    );

    const { data: campaignsData } = useQuery(
        ["social-campaigns", { limit: 5 }],
        () => socialMediaService.listCampaigns({ limit: 5 })
    );

    const { data: upcomingData } = useQuery("social-upcoming", () =>
        socialMediaService.getUpcoming(10)
    );

    const pullAnalyticsMutation = useMutation(
        () => socialMediaService.pullAnalytics(),
        {
            onSuccess: () => {
                toast.success("Analytics refresh started");
                queryClient.invalidateQueries("social-dashboard");
            },
            onError: () => {
                toast.error("Failed to pull analytics");
            },
        }
    );

    if (isLoading) return <Loader />;

    const stats = dashboardData?.data?.stats;
    const analytics = dashboardData?.data?.analytics;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                }}
            >
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: "#0F172A" }}>
                        Social Media Hub
                    </Typography>
                    <Typography sx={{ color: "#64748B", mt: 0.5 }}>
                        AI-powered multi-platform content management
                    </Typography>
                </Box>
                <Box display="flex" gap={1}>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshRoundedIcon />}
                        onClick={() => pullAnalyticsMutation.mutate()}
                        disabled={pullAnalyticsMutation.isLoading}
                    >
                        Sync Analytics
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddRoundedIcon />}
                        onClick={() => navigate("/social-media/campaigns/new")}
                    >
                        New Campaign
                    </Button>
                </Box>
            </Box>

            {/* Stats Grid */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        icon={<ShareRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Connected Platforms"
                        value={stats?.connectedPlatforms || 0}
                        color="#00897B"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        icon={<CampaignRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Total Posts"
                        value={stats?.totalPosts || 0}
                        color="#2563EB"
                        subtitle={`${stats?.pendingPosts || 0} pending`}
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        icon={<VisibilityRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Impressions"
                        value={analytics?.totals?.totalImpressions || 0}
                        color="#7C3AED"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        icon={<TrendingUpRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Engagement Rate"
                        value={analytics?.totals?.engagementRate || "0%"}
                        color="#EA580C"
                        subtitle={`${analytics?.totals?.totalEngagements || 0} engagements`}
                    />
                </Grid>
            </Grid>

            {/* Second Stats Row */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        icon={<CheckCircleRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Posted"
                        value={stats?.postedPosts || 0}
                        color="#16A34A"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        icon={<ScheduleRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Scheduled"
                        value={stats?.pendingPosts || 0}
                        color="#D97706"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        icon={<ThumbUpRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Likes"
                        value={analytics?.totals?.totalLikes || 0}
                        color="#E4405F"
                    />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <StatsCard
                        icon={<TouchAppRoundedIcon sx={{ fontSize: 20 }} />}
                        label="Link Clicks"
                        value={analytics?.totals?.totalClicks || 0}
                        color="#0A66C2"
                    />
                </Grid>
            </Grid>

            <Grid container spacing={3}>
                {/* Platform Health */}
                <Grid item xs={12} md={4}>
                    <Card sx={premiumCard}>
                        <Box
                            sx={{
                                px: 2.5,
                                py: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
                            }}
                        >
                            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                                Platform Health
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => navigate("/social-media/platforms")}
                            >
                                Manage
                            </Button>
                        </Box>
                        <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {stats?.platforms?.length ? (
                                stats.platforms.map((p: any) => (
                                    <PlatformHealthCard
                                        key={p.id}
                                        platform={p.platform}
                                        accountName={p.accountName}
                                        tokenHealthy={p.tokenHealthy}
                                        tokenExpires={p.tokenExpires}
                                    />
                                ))
                            ) : (
                                <Box sx={{ textAlign: "center", py: 4 }}>
                                    <Typography sx={{ color: "#94A3B8", mb: 1 }}>
                                        No platforms connected
                                    </Typography>
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        onClick={() => navigate("/social-media/platforms")}
                                    >
                                        Connect Platform
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </Card>
                </Grid>

                {/* Recent Campaigns */}
                <Grid item xs={12} md={8}>
                    <Card sx={premiumCard}>
                        <Box
                            sx={{
                                px: 2.5,
                                py: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
                            }}
                        >
                            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                                Recent Campaigns
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => navigate("/social-media/campaigns")}
                            >
                                View All
                            </Button>
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Campaign</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Posts</TableCell>
                                    <TableCell>Created</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {campaignsData?.data?.campaigns?.length ? (
                                    campaignsData.data.campaigns.map((c: any) => {
                                        const status =
                                            campaignStatusConfig[c.status] || campaignStatusConfig.draft;
                                        return (
                                            <TableRow
                                                key={c.id}
                                                hover
                                                sx={{ cursor: "pointer" }}
                                                onClick={() =>
                                                    navigate(`/social-media/campaigns/${c.id}`)
                                                }
                                            >
                                                <TableCell>
                                                    <Typography
                                                        sx={{ fontSize: "13px", fontWeight: 600 }}
                                                    >
                                                        {c.title}
                                                    </Typography>
                                                    <Typography
                                                        sx={{ fontSize: "11px", color: "#94A3B8" }}
                                                    >
                                                        {c.source_type}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={status.label}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: status.bg,
                                                            color: status.color,
                                                            fontWeight: 600,
                                                            fontSize: "11px",
                                                        }}
                                                    />
                                                </TableCell>
                                                <TableCell>{c._count?.posts || 0}</TableCell>
                                                <TableCell>
                                                    {new Date(c.created_at).toLocaleDateString()}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                                            <Typography sx={{ color: "#94A3B8" }}>
                                                No campaigns yet
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </Grid>

                {/* Platform Performance */}
                {analytics?.byPlatform &&
                    Object.keys(analytics.byPlatform).length > 0 && (
                        <Grid item xs={12}>
                            <Card sx={premiumCard}>
                                <Box
                                    sx={{
                                        px: 2.5,
                                        py: 2,
                                        borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
                                    }}
                                >
                                    <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                                        Platform Performance (30 days)
                                    </Typography>
                                </Box>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Platform</TableCell>
                                            <TableCell align="right">Posts</TableCell>
                                            <TableCell align="right">Impressions</TableCell>
                                            <TableCell align="right">Engagements</TableCell>
                                            <TableCell align="right">Clicks</TableCell>
                                            <TableCell align="right">Likes</TableCell>
                                            <TableCell align="right">Performance</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {Object.entries(analytics.byPlatform).map(
                                            ([platform, data]: [string, any]) => {
                                                const info = PLATFORM_INFO[platform] || {
                                                    name: platform,
                                                    color: "#666",
                                                    icon: "?",
                                                };
                                                const engRate =
                                                    data.impressions > 0
                                                        ? (
                                                            (data.engagements / data.impressions) *
                                                            100
                                                        ).toFixed(1)
                                                        : "0";
                                                return (
                                                    <TableRow key={platform}>
                                                        <TableCell>
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <Box
                                                                    sx={{
                                                                        width: 28,
                                                                        height: 28,
                                                                        borderRadius: "8px",
                                                                        backgroundColor: alpha(info.color, 0.08),
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        fontSize: "14px",
                                                                    }}
                                                                >
                                                                    {info.icon}
                                                                </Box>
                                                                <Typography sx={{ fontWeight: 600, fontSize: "13px" }}>
                                                                    {info.name}
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                        <TableCell align="right">{data.posts}</TableCell>
                                                        <TableCell align="right">
                                                            {new Intl.NumberFormat().format(data.impressions)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {new Intl.NumberFormat().format(data.engagements)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {new Intl.NumberFormat().format(data.clicks)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            {new Intl.NumberFormat().format(data.likes)}
                                                        </TableCell>
                                                        <TableCell align="right">
                                                            <Box display="flex" alignItems="center" gap={1}>
                                                                <LinearProgress
                                                                    variant="determinate"
                                                                    value={Math.min(parseFloat(engRate), 100)}
                                                                    sx={{
                                                                        width: 60,
                                                                        height: 6,
                                                                        borderRadius: 3,
                                                                        backgroundColor: alpha(info.color, 0.1),
                                                                        "& .MuiLinearProgress-bar": {
                                                                            backgroundColor: info.color,
                                                                            borderRadius: 3,
                                                                        },
                                                                    }}
                                                                />
                                                                <Typography
                                                                    sx={{ fontSize: "12px", fontWeight: 600 }}
                                                                >
                                                                    {engRate}%
                                                                </Typography>
                                                            </Box>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            }
                                        )}
                                    </TableBody>
                                </Table>
                            </Card>
                        </Grid>
                    )}

                {/* Upcoming Posts */}
                <Grid item xs={12}>
                    <Card sx={premiumCard}>
                        <Box
                            sx={{
                                px: 2.5,
                                py: 2,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                borderBottom: "1px solid rgba(226, 232, 240, 0.5)",
                            }}
                        >
                            <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>
                                Upcoming Scheduled Posts
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => navigate("/social-media/calendar")}
                            >
                                View Calendar
                            </Button>
                        </Box>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Platform</TableCell>
                                    <TableCell>Content</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Scheduled</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {upcomingData?.data?.length ? (
                                    upcomingData.data.map((post: any) => {
                                        const info = PLATFORM_INFO[post.platform?.platform] || {
                                            name: "Unknown",
                                            color: "#666",
                                            icon: "?",
                                        };
                                        return (
                                            <TableRow key={post.id}>
                                                <TableCell>
                                                    <Box display="flex" alignItems="center" gap={1}>
                                                        <span>{info.icon}</span>
                                                        <Typography sx={{ fontSize: "13px" }}>
                                                            {info.name}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Typography
                                                        sx={{
                                                            fontSize: "13px",
                                                            maxWidth: 400,
                                                            overflow: "hidden",
                                                            textOverflow: "ellipsis",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {post.content?.substring(0, 100)}
                                                        {post.content?.length > 100 ? "..." : ""}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={post.status}
                                                        size="small"
                                                        sx={{ fontSize: "11px" }}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {post.scheduled_at
                                                        ? new Date(post.scheduled_at).toLocaleString()
                                                        : "-"}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4} sx={{ textAlign: "center", py: 4 }}>
                                            <Typography sx={{ color: "#94A3B8" }}>
                                                No upcoming posts
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default SocialDashboard;
